import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Renders prompt + response using the same Message/StreamingMessages pipeline
 * as ConversationWidget, for static content (e.g. prompt test results in admin).
 * Supports ItemReference / ItemList in the response when itemReferences is provided.
 */
import { useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { ConversationModel, ConversationMessage, } from '../models/ConversationModel';
import { ConversationStoreProvider } from '../ConversationStoreProvider';
import { TokensProvider } from '../tokens/TokensProvider';
import Tokens, { defaultTokens } from '../tokens/Tokens';
import StreamingMessages from './StreamingMessages';
const defaultQuestionStyle = {
    backgroundColor: '#dcdcdc',
    borderColor: '#9b9b9b',
    borderRadius: 8,
    borderWidth: 1,
    color: 'color_font',
    margin: 0,
    padding: 12,
    shadow: 1,
};
const defaultResponseStyle = {
    backgroundColor: 'color_background',
    borderColor: '#9b9b9b',
    borderRadius: 8,
    borderWidth: 1,
    color: 'color_font',
    margin: 0,
    padding: 12,
    shadow: 1,
};
function createStaticStore(conversation, stylesheet, tokens) {
    return {
        amplifyEnvironment: 'prod',
        currentPortalID: '',
        currentOrganizationID: '',
        clientAIUrl: '',
        clientAIContentUrl: '',
        clientAIBaseUrl: '',
        appendCustomerContext: () => { },
        createConversation: () => undefined,
        setCurrentConversation: () => undefined,
        currentConversation: conversation,
        stylesheet,
        promptPlaceholder: '',
        clientAIFeedbackButtons: false,
        isAIServiceUnavailable: false,
        clientAIConfig: {},
        clientAISuggestedPrompts: [],
        clientAIStartingMessage: '',
        clientAIPersistentPrompts: '',
        customerContext: null,
        currentUser: null,
        widgetProps: {},
        suggestedPrompts: [],
        suggestedPromptsHeader: '',
        smartSummary: null,
        isLoadingSuggestedPrompts: false,
        lastSuggestedPromptsKey: '',
        setSuggestedPrompts: () => { },
        setSmartSummary: () => { },
        requestPageSpecificPrompts: async () => { },
        requestPageAdditionalData: async () => { },
        tokens,
        defaultLocale: 'en',
    };
}
const StaticMessageBubblesInner = ({ className, prompt, response, itemReferences: itemReferencesProp, questionStyle: questionStyleProp, responseStyle: responseStyleProp, stylesheet = {}, showFeedbackButtons = false, feedback = null, onFeedback, hideQuestion = false, onRefinementSelect, dense = false, questionAlignment = 'left', showCloseButton = false, onClose, }) => {
    const conversation = useMemo(() => {
        const assistantMsg = ConversationMessage.create({
            id: `static-assistant-${Date.now()}`,
            content: response,
            role: 'assistant',
            ...(feedback ? { feedback } : {}),
        });
        return ConversationModel.create({
            id: `static-${Date.now()}`,
            createdAt: new Date(),
            updatedAt: new Date(),
            messages: hideQuestion
                ? [assistantMsg]
                : [
                    ConversationMessage.create({
                        id: `static-user-${Date.now()}`,
                        content: prompt,
                        role: 'user',
                    }),
                    assistantMsg,
                ],
        });
    }, [prompt, response, feedback, hideQuestion]);
    useEffect(() => {
        if (itemReferencesProp && Object.keys(itemReferencesProp).length > 0) {
            conversation.appendReferences(itemReferencesProp);
        }
    }, [conversation, itemReferencesProp]);
    const store = useMemo(() => {
        const tokens = new Tokens({ tokens: defaultTokens, defaultLocale: 'en' });
        return createStaticStore(conversation, stylesheet, tokens);
    }, [conversation, stylesheet]);
    const hasItemReferences = Boolean(itemReferencesProp && Object.keys(itemReferencesProp).length > 0);
    const messageConfig = useMemo(() => ({
        openSupportToolDelay: 6000,
        openSupportToolMessage: '',
        showItemReferences: hasItemReferences,
        showFeedbackButtons,
        questionStyle: questionStyleProp ?? defaultQuestionStyle,
        responseStyle: responseStyleProp ?? defaultResponseStyle,
        questionAlignment,
        responseAlignment: 'left',
        openWebPagesInNewTab: true,
        hideRightRailOnMobile: true,
        maxItemsInRightRail: 0,
        enableMultiRefinements: false,
        multiRefinementSubmitButtonText: 'Submit',
        itemReferenceDisplay: 'card',
        dualModeEnabled: false,
        onRefinementClick: onRefinementSelect,
        showCloseButton,
        onClose,
    }), [questionStyleProp, responseStyleProp, hasItemReferences, showFeedbackButtons, onRefinementSelect, questionAlignment, showCloseButton, onClose]);
    return (_jsx(ConversationStoreProvider, { value: store, children: _jsx(TokensProvider, { value: store.tokens, children: _jsx(Box, { className: className, sx: { width: '100%' }, children: _jsx(Stack, { spacing: dense ? 0.5 : 2, sx: { padding: dense ? 0 : 1 }, children: _jsx(StreamingMessages, { messageConfig: messageConfig, isLoading: false, saveFeedback: (type) => {
                            onFeedback?.(type);
                        }, isMobileView: false, hideRightRailOnMobile: true, maxItemsInRightRail: hasItemReferences ? 8 : 0, disableAutoScroll: true, messageStackMode: "natural" }) }) }) }) }));
};
const StaticMessageBubbles = (props) => (
// eslint-disable-next-line react/jsx-props-no-spreading
_jsx(StaticMessageBubblesInner, { ...props }));
StaticMessageBubbles.defaultProps = {
    className: undefined,
    itemReferences: undefined,
    questionStyle: defaultQuestionStyle,
    responseStyle: defaultResponseStyle,
    stylesheet: {},
    showFeedbackButtons: false,
    feedback: null,
    onFeedback: undefined,
    hideQuestion: false,
    onRefinementSelect: undefined,
    dense: false,
    questionAlignment: 'left',
    showCloseButton: false,
    onClose: undefined,
};
export default StaticMessageBubbles;
//# sourceMappingURL=StaticMessageBubbles.js.map